import React, { useState } from "react";
import { Link } from "react-router-dom";
import Dialog from "../ui/Dialog";
import { formatDateTime, fromNow } from "../../utils/dateTime";
import { useBirthdays } from "../../api/queries/widgetQueries";
import { avatarColor, properName } from "../../utils/text";
import {
  birthdayLink,
  dateBadge,
  daysAwayText,
  groupBirthdays,
  holidayBadge,
  initialsOf,
  shortDate,
} from "./widgetFormat";
import "./WidgetLists.css";

const EMPTY = [];

const Empty = ({ icon, text }) => (
  <div className="dw-empty">
    <i className={`bi ${icon}`} aria-hidden="true"></i>
    {text}
  </div>
);

const DateBadge = ({ ymd, tone }) => {
  const b = dateBadge(ymd);
  return (
    <div className={`dw-date ${tone ? `dw-date--${tone}` : ""}`}>
      <span className="dw-date-month">{b.month}</span>
      <span className="dw-date-day">{b.day}</span>
    </div>
  );
};

/** Photo, else initials on a colour that stays the same for the person. */
export const Avatar = ({ id, name, photoUrl, size }) =>
  photoUrl ? (
    <img
      className={`dw-avatar ${size === "lg" ? "dw-avatar--lg" : ""}`}
      src={photoUrl}
      alt={name}
    />
  ) : (
    <span
      className={`dw-avatar ${size === "lg" ? "dw-avatar--lg" : ""}`}
      style={{ background: avatarColor(id || name) }}
      role="img"
      aria-label={name}
    >
      {initialsOf(name)}
    </span>
  );

/* ---------- Announcements ---------- */

const AnnouncementModal = ({ a, onClose }) => (
  <Dialog
    title={a.title}
    subtitle={[
      properName(a.createdBy?.name),
      a.companyName,
      a.publishAt && formatDateTime(a.publishAt),
    ]
      .filter(Boolean)
      .join(" · ")}
    onClose={onClose}
    size="lg"
  >
    {a.priority === "IMPORTANT" && (
      <span className="status-pill status-pill--red mb-2 d-inline-block">
        Important
      </span>
    )}
    <p className="dw-ann-full">{a.body}</p>
  </Dialog>
);

/** Pinned first (server order); 2 lines, Read more opens the full text. */
const Announcement = ({ a }) => {
  const [open, setOpen] = useState(false);
  const long =
    (a.body || "").length > 140 || (a.body || "").split("\n").length > 2;
  return (
    <li
      className={`dw-ann ${a.priority === "IMPORTANT" ? "is-important" : ""}`}
    >
      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
        {a.pinned && (
          <i
            className="bi bi-pin-angle-fill text-primary"
            title="Pinned"
            aria-label="Pinned"
          ></i>
        )}
        <strong className="dw-ann-title">{a.title}</strong>
        {a.priority === "IMPORTANT" && (
          <span className="status-pill status-pill--red">Important</span>
        )}
      </div>
      <p className="dw-ann-body is-folded">{a.body}</p>
      <div className="dw-meta d-flex flex-wrap gap-2 align-items-center">
        <span>
          {[properName(a.createdBy?.name), a.companyName]
            .filter(Boolean)
            .join(" · ")}
        </span>
        {a.publishAt && (
          <span title={formatDateTime(a.publishAt)}>
            {fromNow(a.publishAt)}
          </span>
        )}
        {long && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 fs-075"
            onClick={() => setOpen(true)}
          >
            Read more
          </button>
        )}
      </div>
      {open && <AnnouncementModal a={a} onClose={() => setOpen(false)} />}
    </li>
  );
};

export const AnnouncementList = ({ items = EMPTY }) =>
  !items?.length ? (
    <Empty icon="bi-megaphone" text="No announcements right now." />
  ) : (
    <ul className="dw-list">
      {items.map((a) => (
        <Announcement key={a.id} a={a} />
      ))}
    </ul>
  );

/* ---------- Birthdays ---------- */

/** A row (or tile) that links to the profile when allowed; otherwise a hover card. */
const BirthdayTarget = ({ b, className, children }) => {
  const to = birthdayLink(b);
  if (to)
    return (
      <Link
        to={to}
        className={`${className} dw-link`}
        aria-label={`${properName(b.name)}${b.isMe ? " (you)" : ""}: open profile`}
      >
        {children}
      </Link>
    );
  return (
    <div className={`${className} dw-hover`} tabIndex={0}>
      {children}
      <div className="dw-card" role="tooltip">
        <Avatar
          id={b.employeeId}
          name={properName(b.name)}
          photoUrl={b.photoUrl}
          size="lg"
        />
        <div className="min-w-0">
          <div className="dw-name">{properName(b.name)}</div>
          {b.jobTitle && <div className="dw-meta">{b.jobTitle}</div>}
          {(b.branchName || b.companyName) && (
            <div className="dw-meta">
              {[b.branchName, b.companyName].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BirthdayRow = ({ b }) => (
  <li>
    <BirthdayTarget b={b} className="dw-row">
      <Avatar
        id={b.employeeId}
        name={properName(b.name)}
        photoUrl={b.photoUrl}
      />
      <div className="min-w-0 flex-grow-1">
        <div className="dw-name text-truncate">
          {properName(b.name)}
          {b.isMe && <span className="dw-me">You</span>}
        </div>
        <div className="dw-meta text-truncate">
          {[b.jobTitle, b.branchName].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="text-end">
        <div className="dw-when">{shortDate(b.date)}</div>
        <div className="dw-meta">{daysAwayText(b.daysAway)}</div>
      </div>
    </BirthdayTarget>
  </li>
);

const TODAY_SHOWN = 3;

/** Today's celebrations first (3, then "+N more today"), then This week / Later this month. */
export const BirthdayList = ({ items = EMPTY }) => {
  const [allToday, setAllToday] = useState(false);
  if (!items?.length)
    return <Empty icon="bi-balloon" text="No birthdays in the next 30 days." />;
  const g = groupBirthdays(items);
  const todayShown = allToday ? g.today : g.today.slice(0, TODAY_SHOWN);
  const moreToday = g.today.length - todayShown.length;
  return (
    <>
      {g.today.length > 0 && (
        <div className="dw-today">
          <div className="dw-section">
            <span role="img" aria-label="Birthday cake">
              🎂
            </span>{" "}
            Today
          </div>
          <ul className="dw-list">
            {todayShown.map((b) => (
              <li key={b.employeeId}>
                <BirthdayTarget b={b} className="dw-row dw-row--today">
                  <Avatar
                    id={b.employeeId}
                    name={properName(b.name)}
                    photoUrl={b.photoUrl}
                    size="lg"
                  />
                  <div className="min-w-0">
                    <div className="dw-name text-truncate">
                      {properName(b.name)}
                      {b.isMe && <span className="dw-me">You</span>}
                    </div>
                    <div className="dw-meta text-truncate">
                      {[b.jobTitle, b.branchName].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </BirthdayTarget>
              </li>
            ))}
          </ul>
          {moreToday > 0 && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 fs-080"
              onClick={() => setAllToday(true)}
            >
              +{moreToday} more today
            </button>
          )}
        </div>
      )}
      {[
        ["This week", g.thisWeek],
        ["Later this month", g.later],
      ].map(
        ([label, list]) =>
          list.length > 0 && (
            <div key={label}>
              <div className="dw-section">{label}</div>
              <ul className="dw-list">
                {list.map((b) => (
                  <BirthdayRow key={b.employeeId} b={b} />
                ))}
              </ul>
            </div>
          ),
      )}
    </>
  );
};

/* ---------- Holidays ---------- */

/** Date block, name, type badge, "Today" / "In n days". */
export const HolidayList = ({ items = EMPTY }) =>
  !items?.length ? (
    <Empty icon="bi-calendar-heart" text="No upcoming holidays." />
  ) : (
    <ul className="dw-list">
      {items.map((h) => {
        const badge = holidayBadge(h);
        return (
          <li key={h.id} className="dw-row">
            <DateBadge ymd={h.date} tone={badge.tone} />
            <div className="min-w-0 flex-grow-1">
              <div className="dw-name text-truncate">{h.name}</div>
              <span className={`status-pill status-pill--${badge.tone}`}>
                {badge.label}
              </span>
            </div>
            <div className="dw-when text-end">
              {daysAwayText(h.daysAway)}
              <div className="dw-meta">{dateBadge(h.date).weekday}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );

/** "View all": every birthday in the next 30 days, in a modal. */
export const BirthdaysModal = ({ onClose, scope }) => {
  const q = useBirthdays(30, { scope });
  return (
    <Dialog
      title="Birthdays"
      subtitle="The next 30 days"
      onClose={onClose}
      size="lg"
    >
      {q.isLoading ? (
        <div className="text-center py-4">
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      ) : q.isError ? (
        <p className="fs-085 text-danger mb-0">Couldn&apos;t load birthdays.</p>
      ) : (
        <BirthdayList items={q.data || EMPTY} />
      )}
    </Dialog>
  );
};
